import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import { formatSocialTimestamp } from "../../utils/dateFormatter";
import ConfirmModal from "../common/ConfirmModal";
import ReportModal from "../moderation/ReportModal";
import MentionDropdown from "../common/MentionDropdown";
import { useMentionSearch } from "../../hooks/useMentionSearch";
import { renderMentionText } from "../../utils/renderMentionText";
import {
  FaHeart,
  FaRegHeart,
  FaTimes,
  FaPaperPlane,
  FaReply,
  FaFlag,
  FaEllipsisV,
  FaEdit,
  FaStar,
} from "react-icons/fa";
import styles from "../../styles/components/profile/PostComments.module.css";

const PostComments = ({
  postId,
  postOwnerId,
  onCommentAdded,
  onCommentDeleted,
  onCommentsLoaded,
}) => {
  const { user: currentUser } = useUserAuth();
  const { showToast } = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [submittingReply, setSubmittingReply] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportCommentId, setReportCommentId] = useState(null);
  const [showMenuForComment, setShowMenuForComment] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editTexts, setEditTexts] = useState({});
  const [submittingEdit, setSubmittingEdit] = useState({});
  const editTextareaRef = useRef(null);
  const commentInputRef = useRef(null);
  const replyInputRef = useRef(null);
  const {
    suggestions: mentionSuggestions,
    mentionQuery,
    insertMention,
    handleInput: handleMentionInput,
    clearMention,
    serializeForSubmit,
    clearMentionRegistry,
  } = useMentionSearch(commentText, setCommentText, commentInputRef);

  useEffect(() => {
    fetchComments();
  }, [postId]);

  // Auto-resize edit textarea
  const autoResizeTextarea = () => {
    if (editTextareaRef.current) {
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = editTextareaRef.current.scrollHeight + 'px';
    }
  };

  useLayoutEffect(() => {
    autoResizeTextarea();
  }, [editTexts]);

  const fetchComments = async (pageNum = 1) => {
    try {
      setLoading(true);
      const res = await api.get(`/posts/${postId}/comments`, {
        params: { page: pageNum, limit: 10 },
      });

      if (pageNum === 1) {
        setComments(res.data.comments || []);
        // Recursively calculate total count including all nested replies
        const countComments = (commentList) => {
          let count = 0;
          commentList.forEach((comment) => {
            count += 1; // Count the comment itself
            if (comment.replies && comment.replies.length > 0) {
              count += countComments(comment.replies); // Recursively count replies
            }
          });
          return count;
        };
        const totalCount = countComments(res.data.comments || []);

        // Always notify parent of the actual calculated count from loaded comments
        // This ensures the card count matches the real count (calculated from actual data)
        // This is more accurate than the backend count which might be stale
        if (onCommentsLoaded) {
          onCommentsLoaded(totalCount);
        }
      } else {
        setComments((prev) => [...prev, ...(res.data.comments || [])]);
      }
      setHasMore(res.data.pagination.page < res.data.pagination.pages);
    } catch (err) {
      console.error("Error fetching comments:", err);
      showToast({
        message: err.response?.data?.message || "Failed to load comments",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      showToast({
        message: "Please log in to comment",
        type: "info",
      });
      return;
    }

    if (!commentText.trim()) {
      return;
    }

    // Optimistic update: increment counter immediately
    onCommentAdded?.({ optimistic: true });

    try {
      setSubmitting(true);
      const res = await api.post(`/posts/${postId}/comments`, {
        text: serializeForSubmit(commentText.trim()),
      });

      setComments((prev) => [res.data, ...prev]);
      setCommentText("");
      clearMentionRegistry();
      // API succeeded, no need to call onCommentAdded again as it was already called optimistically
    } catch (err) {
      console.error("Error adding comment:", err);
      showToast({
        message: err.response?.data?.message || "Failed to add comment",
        type: "error",
      });
      // API failed, revert the optimistic update
      onCommentDeleted?.(1);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!submitting && commentText.trim()) {
        handleSubmit(e);
      }
    }
  };

  const handleDeleteClick = (commentId) => {
    setCommentToDelete({ commentId, parentCommentId: null });
  };

  const handleCommentLike = async (commentId, isLiked) => {
    if (!currentUser) {
      showToast({
        message: "Please log in to like comments",
        type: "info",
      });
      return;
    }

    try {
      const res = await api.post(`/posts/comments/${commentId}/like`);

      // Recursively update like status in the comment tree
      const updateLikeInTree = (commentList, targetId, likeData) => {
        return commentList.map((comment) => {
          if (comment._id === targetId) {
            return {
              ...comment,
              isLiked: likeData.isLiked,
              likeCount: likeData.likeCount,
            };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: updateLikeInTree(comment.replies, targetId, likeData),
            };
          }
          return comment;
        });
      };

      setComments((prev) => updateLikeInTree(prev, commentId, res.data));
    } catch (err) {
      console.error("Error liking comment:", err);
      showToast({
        message: err.response?.data?.message || "Failed to like comment",
        type: "error",
      });
    }
  };

  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    if (replyingTo !== commentId) {
      setReplyTexts({ ...replyTexts, [commentId]: "" });
    }
  };

  useEffect(() => {
    if (!replyingTo) return;
    replyInputRef.current?.focus();
  }, [replyingTo]);

  const handleReplySubmit = async (e, parentCommentId) => {
    e.preventDefault();

    if (!currentUser) {
      showToast({
        message: "Please log in to reply",
        type: "info",
      });
      return;
    }

    const replyText = replyTexts[parentCommentId]?.trim();
    if (!replyText) {
      return;
    }

    // Optimistic update: increment counter immediately
    onCommentAdded?.({ optimistic: true });

    try {
      setSubmittingReply({ ...submittingReply, [parentCommentId]: true });
      const res = await api.post(`/posts/${postId}/comments`, {
        text: replyText,
        parentComment: parentCommentId,
      });

      // Recursively add reply to the parent comment in the tree
      const addReplyToTree = (commentList, parentId, newReply) => {
        return commentList.map((comment) => {
          if (comment._id === parentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), newReply],
            };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: addReplyToTree(comment.replies, parentId, newReply),
            };
          }
          return comment;
        });
      };

      setComments((prev) => addReplyToTree(prev, parentCommentId, res.data));

      setReplyTexts({ ...replyTexts, [parentCommentId]: "" });
      setReplyingTo(null);
      // API succeeded, no need to call onCommentAdded again as it was already called optimistically
    } catch (err) {
      console.error("Error adding reply:", err);
      showToast({
        message: err.response?.data?.message || "Failed to add reply",
        type: "error",
      });
      // API failed, revert the optimistic update
      onCommentDeleted?.(1);
    } finally {
      setSubmittingReply({ ...submittingReply, [parentCommentId]: false });
    }
  };

  const handleDeleteReply = (commentId, parentCommentId) => {
    setCommentToDelete({ commentId, parentCommentId });
  };

  const handleEditClick = (commentId, currentText) => {
    setEditingCommentId(editingCommentId === commentId ? null : commentId);
    if (editingCommentId !== commentId) {
      setEditTexts({ ...editTexts, [commentId]: currentText });
    }
  };

  const handleEditSubmit = async (e, commentId) => {
    e.preventDefault();

    if (!currentUser) {
      showToast({
        message: "Please log in to edit comments",
        type: "info",
      });
      return;
    }

    const editText = editTexts[commentId]?.trim();
    if (!editText) {
      return;
    }

    try {
      setSubmittingEdit({ ...submittingEdit, [commentId]: true });
      const res = await api.put(`/posts/comments/${commentId}`, {
        text: editText.trim(),
      });

      // Recursively update comment text in the comment tree
      const updateCommentInTree = (commentList, targetId, updatedComment) => {
        return commentList.map((comment) => {
          if (comment._id === targetId) {
            return { ...comment, ...updatedComment };
          }
          if (comment.replies && comment.replies.length > 0) {
            return {
              ...comment,
              replies: updateCommentInTree(comment.replies, targetId, updatedComment),
            };
          }
          return comment;
        });
      };

      setComments((prev) => updateCommentInTree(prev, commentId, res.data));
      setEditingCommentId(null);
      setEditTexts({ ...editTexts, [commentId]: "" });
      showToast({
        message: "Comment updated successfully",
        type: "success",
      });
    } catch (err) {
      console.error("Error editing comment:", err);
      showToast({
        message: err.response?.data?.message || "Failed to edit comment",
        type: "error",
      });
    } finally {
      setSubmittingEdit({ ...submittingEdit, [commentId]: false });
    }
  };

  const handleEditCancel = (commentId) => {
    setEditingCommentId(null);
    setEditTexts({ ...editTexts, [commentId]: "" });
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete) return;

    const { commentId, parentCommentId } = commentToDelete;

    // Recursively count all nested replies that will be deleted
    const countNestedReplies = (comment) => {
      let count = 1; // Count the comment itself
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => {
          count += countNestedReplies(reply);
        });
      }
      return count;
    };

    // Find the comment to delete to count its nested replies
    const findComment = (commentList, targetId) => {
      for (const c of commentList) {
        if (c._id === targetId) return c;
        if (c.replies && c.replies.length > 0) {
          const found = findComment(c.replies, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const commentToDeleteObj = findComment(comments, commentId);
    const totalToDelete = commentToDeleteObj
      ? countNestedReplies(commentToDeleteObj)
      : 1;

    // Optimistic update: decrement counter immediately
    onCommentDeleted?.(totalToDelete);

    try {
      await api.delete(`/posts/comments/${commentId}`);

      // Recursively remove comment and all its nested replies from the state
      const removeCommentFromTree = (commentList, targetId) => {
        return commentList
          .filter((c) => c._id !== targetId)
          .map((c) => {
            if (c.replies && c.replies.length > 0) {
              return {
                ...c,
                replies: removeCommentFromTree(c.replies, targetId),
              };
            }
            return c;
          });
      };

      if (parentCommentId) {
        // Delete reply (and all its nested replies) from parent comment
        const updatedComments = comments.map((c) => {
          if (c._id === parentCommentId) {
            return {
              ...c,
              replies: removeCommentFromTree(c.replies || [], commentId),
            };
          }
          // Also check nested replies in case we're deleting a nested reply
          if (c.replies && c.replies.length > 0) {
            return {
              ...c,
              replies: removeCommentFromTree(c.replies, commentId),
            };
          }
          return c;
        });
        setComments(updatedComments);
      } else {
        // Delete top-level comment (and all its nested replies)
        const updatedComments = removeCommentFromTree(comments, commentId);
        setComments(updatedComments);
      }

      // API succeeded, no need to call onCommentDeleted again as it was already called optimistically
      setCommentToDelete(null);
    } catch (err) {
      console.error("Error deleting comment:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete comment",
        type: "error",
      });
      // API failed, revert the optimistic update by adding back the deleted count
      onCommentAdded?.({ optimistic: true });
      setCommentToDelete(null);
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchComments(nextPage);
  };

  // Render a comment - main comments at level 0, all replies at level 1
  const renderComment = (comment, isReply = false) => {
    const isOwnComment =
      currentUser &&
      comment.user &&
      (currentUser.id === comment.user._id ||
        currentUser._id === comment.user._id);

    const isPostOwner =
      currentUser &&
      postOwnerId &&
      (currentUser.id === postOwnerId || currentUser._id === postOwnerId);

    const canDelete = isOwnComment || isPostOwner;
    const canReply = currentUser; // Anyone can reply now

    // Recursively collect all replies (flatten nested structure)
    // Use a Set to track seen IDs to prevent duplicates
    const getAllRepliesFlattened = (
      replyList,
      seenIds = new Set(),
      allReplies = [],
    ) => {
      replyList
        .filter((reply) => reply && reply.user)
        .forEach((reply) => {
          // Only add if we haven't seen this reply ID before
          if (!seenIds.has(reply._id)) {
            seenIds.add(reply._id);
            // Create a copy without nested replies to prevent recursive rendering
            const replyWithoutNested = { ...reply, replies: [] };
            allReplies.push(replyWithoutNested);
            if (reply.replies && reply.replies.length > 0) {
              getAllRepliesFlattened(reply.replies, seenIds, allReplies);
            }
          }
        });
      return allReplies;
    };

    const handleDeleteClickWithParent = (commentId) => {
      // Find parent ID by searching the tree
      const findParent = (comments, targetId, parentId = null) => {
        for (const c of comments) {
          if (c._id === targetId) return parentId;
          if (c.replies && c.replies.length > 0) {
            const found = findParent(c.replies, targetId, c._id);
            if (found !== null) return found;
          }
        }
        return null;
      };
      const parentId = findParent(comments, commentId);
      if (parentId) {
        handleDeleteReply(commentId, parentId);
      } else {
        handleDeleteClick(commentId);
      }
    };

    return (
      <div
        key={comment._id}
        className={isReply ? styles.replyItem : styles.commentItem}
      >
        <Link
          to={comment.user ? `/user/${comment.user._id}` : "7"}
          className={
            isReply ? styles.replyUserAvatar : styles.commentUserAvatar
          }
        >
          {comment.user?.avatar ? (
            <img
              src={getImageUrl(comment.user.avatar)}
              alt={comment.user?.firstName || "User"}
            />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {comment.user?.firstName?.[0] || "U"}
            </div>
          )}
        </Link>
        <div className={isReply ? styles.replyContent : styles.commentContent}>
          <div className={isReply ? styles.replyHeader : styles.commentHeader}>
            <Link
              to={comment.user ? `/user/${comment.user._id}` : "7"}
              className={
                isReply ? styles.replyUserName : styles.commentUserName
              }
            >
              {comment.user?.firstName || "Unknown"}{" "}
              {comment.user?.lastName || "User"}
              {comment.user?.expert?.status === "approved" && (
                <span className={styles.expertBadge} title="Verified Expert">
                  <FaStar size={8} /> Expert
                </span>
              )}
            </Link>
            <div className={styles.commentMenuContainer}>
              {canDelete && (
                <button
                  type="button"
                  className={`${styles.deleteCommentButton} buttonIcon buttonSmall`}
                  onClick={() => handleDeleteClickWithParent(comment._id)}
                  title={
                    isPostOwner && !isOwnComment
                      ? "Delete comment (post owner)"
                      : "Delete comment"
                  }
                >
                  <FaTimes />
                </button>
              )}
              {!isOwnComment && (
                <button
                  type="button"
                  className={styles.commentMenuButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenuForComment(
                      showMenuForComment === comment._id ? null : comment._id,
                    );
                  }}
                  title="More options"
                >
                  <FaEllipsisV />
                </button>
              )}
              {showMenuForComment === comment._id && (
                <div className={styles.commentMenuDropdown}>
                  <button
                    type="button"
                    className={styles.commentMenuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenuForComment(null);
                      setReportCommentId(comment._id);
                      setShowReportModal(true);
                    }}
                  >
                    <FaFlag /> Report Comment
                  </button>
                </div>
              )}
            </div>
          </div>
          {editingCommentId === comment._id ? (
            <form
              onSubmit={(e) => handleEditSubmit(e, comment._id)}
              className={styles.editForm}
            >
              <textarea
                ref={editingCommentId === comment._id ? editTextareaRef : null}
                value={editTexts[comment._id] || ""}
                onChange={(e) => {
                  setEditTexts({
                    ...editTexts,
                    [comment._id]: e.target.value,
                  });
                  // Trigger auto-resize on change
                  setTimeout(autoResizeTextarea, 0);
                }}
                onKeyDown={(e) => {
                  // Keep Space/Enter from activating a parent post card.
                  e.stopPropagation();
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (
                      !submittingEdit[comment._id] &&
                      editTexts[comment._id]?.trim()
                    ) {
                      handleEditSubmit(e, comment._id);
                    }
                  } else if (e.key === "Escape") {
                    handleEditCancel(comment._id);
                  }
                }}
                placeholder="Edit your comment..."
                className={styles.editInput}
                rows={3}
                maxLength={500}
                autoFocus
              />
              <div className={styles.editFormActions}>
                <button
                  type="button"
                  className={styles.cancelEditButton}
                  onClick={() => handleEditCancel(comment._id)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.saveEditButton}
                  disabled={
                    submittingEdit[comment._id] ||
                    !editTexts[comment._id]?.trim() ||
                    editTexts[comment._id]?.trim() === comment.text
                  }
                >
                  <FaPaperPlane />
                  {submittingEdit[comment._id] ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          ) : (
            <p className={isReply ? styles.replyText : styles.commentText}>
              {renderMentionText(comment.text)}
            </p>
          )}
          <div
            className={isReply ? styles.replyActions : styles.commentActions}
          >
            <button
              type="button"
              className={`${styles.commentLikeButton} ${
                comment.isLiked ? styles.liked : ""
              }`}
              onClick={() => handleCommentLike(comment._id, comment.isLiked)}
            >
              {comment.isLiked ? <FaHeart /> : <FaRegHeart />}
              <span>{comment.likeCount || 0}</span>
            </button>
            {isOwnComment && (
              <button
                type="button"
                className={styles.editButton}
                onClick={() => handleEditClick(comment._id, comment.text)}
                title="Edit comment"
              >
                <FaEdit />
                <span>Edit</span>
              </button>
            )}
            {canReply && (
              <button
                type="button"
                className={styles.replyButton}
                onClick={() => handleReplyClick(comment._id)}
                title="Reply to this comment"
              >
                <FaReply />
                <span>Reply</span>
              </button>
            )}
            <span className={styles.commentTime}>
              {formatSocialTimestamp(comment.createdAt)}
            </span>
          </div>

          {/* Render all replies at the same level (second level) - flatten nested structure */}
          {comment.replies && comment.replies.length > 0 && (
            <div className={styles.repliesContainer}>
              {getAllRepliesFlattened(comment.replies).map((r) =>
                renderComment(r, true),
              )}
            </div>
          )}

          {/* Reply Form (anyone can reply) - Show after replies to keep it at the end */}
          {canReply && replyingTo === comment._id && (
            <form
              onSubmit={(e) => handleReplySubmit(e, comment._id)}
              className={styles.replyForm}
            >
              <textarea
                ref={replyingTo === comment._id ? replyInputRef : null}
                value={replyTexts[comment._id] || ""}
                onChange={(e) =>
                  setReplyTexts({
                    ...replyTexts,
                    [comment._id]: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  // The post card treats Space as a click. Stop it here so
                  // Chrome inserts the character instead of swallowing it.
                  e.stopPropagation();
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (
                      !submittingReply[comment._id] &&
                      replyTexts[comment._id]?.trim()
                    ) {
                      handleReplySubmit(e, comment._id);
                    }
                  }
                }}
                placeholder="Write a reply..."
                className={styles.replyInput}
                rows={2}
                maxLength={500}
              />
              <div className={styles.replyFormActions}>
                <button
                  type="button"
                  className={styles.cancelReplyButton}
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyTexts({ ...replyTexts, [comment._id]: "" });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.replySubmitButton}
                  disabled={
                    submittingReply[comment._id] ||
                    !replyTexts[comment._id]?.trim()
                  }
                >
                  <FaPaperPlane />
                  {submittingReply[comment._id] ? "Posting..." : "Reply"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="comments-section" className={styles.commentsSection}>
      {/* Comment Form */}
      {currentUser && (
        <form onSubmit={handleSubmit} className={styles.commentForm}>
          <div className={styles.commentInputWrapper}>
            <textarea
              ref={commentInputRef}
              value={commentText}
              onChange={(e) => {
                setCommentText(e.target.value);
                handleMentionInput(e);
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") clearMention();
                handleCommentKeyDown(e);
              }}
              placeholder="Write a comment… type @ to tag a product"
              className={styles.commentInput}
              rows={2}
              maxLength={500}
            />
            {mentionQuery !== null && (
              <MentionDropdown
                suggestions={mentionSuggestions}
                query={mentionQuery}
                onSelect={(product) => insertMention(product)}
              />
            )}
          </div>
          <button
            type="submit"
            className={styles.commentSubmit}
            disabled={submitting || !commentText.trim()}
          >
            <FaPaperPlane />
            {submitting ? "Posting..." : "Post"}
          </button>
        </form>
      )}

      {/* Comments List - Main comments at level 0, all replies at level 1 */}
      {loading ? (
        <div className={styles.loading}>Loading comments...</div>
      ) : comments.length > 0 ? (
        <>
          <div className={styles.commentsList}>
            {comments
              .filter((comment) => comment && comment.user)
              .map((comment) => renderComment(comment, false))}
          </div>
          {hasMore && (
            <button
              type="button"
              className={styles.loadMoreButton}
              onClick={loadMore}
            >
              Load More Comments
            </button>
          )}
        </>
      ) : (
        <div className={styles.emptyState}>
          <p>No comments yet. Be the first to comment!</p>
        </div>
      )}

      {/* Delete Comment Confirmation Modal */}
      {commentToDelete && (
        <ConfirmModal
          message="Are you sure you want to delete this comment?"
          onConfirm={handleDeleteConfirm}
          onClose={() => setCommentToDelete(null)}
        />
      )}

      {/* Report Modal */}
      {showReportModal && reportCommentId && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportCommentId(null);
          }}
          contentType="comment"
          contentId={reportCommentId}
          onReported={() => {
            setShowReportModal(false);
            setReportCommentId(null);
          }}
        />
      )}
    </div>
  );
};

export default PostComments;
